import { DEG } from "./time";

export interface PlaneElements {
  i: number;
  node: number;
  omega: number;
}

type V3 = [number, number, number];
const OBLIQUITY = 23.4392911 * DEG;

function rotX(v: V3, a: number): V3 {
  const c = Math.cos(a),
    s = Math.sin(a);
  return [v[0], c * v[1] - s * v[2], s * v[1] + c * v[2]];
}
function rotZ(v: V3, a: number): V3 {
  const c = Math.cos(a),
    s = Math.sin(a);
  return [c * v[0] - s * v[1], s * v[0] + c * v[1], v[2]];
}
function cross(a: V3, b: V3): V3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}
function dot(a: V3, b: V3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}
function deg360(rad: number): number {
  const d = (rad / DEG) % 360;
  return d < 0 ? d + 360 : d;
}

/** Orbit normal and perihelion direction for elements (radians) in their own reference plane. */
function orbitAxes(i: number, node: number, omega: number): { normal: V3; peri: V3 } {
  const toPlane = (v: V3): V3 => rotZ(rotX(rotZ(v, omega), i), node);
  return { normal: toPlane([0, 0, 1]), peri: toPlane([1, 0, 0]) };
}

/**
 * Rotates orbital elements given relative to a reference plane (pole at RA/Dec in ICRF, node
 * measured from the plane's ascending node on the ICRF equator) into the J2000 ecliptic.
 * Plane to ICRF is Rz(RA + 90) . Rx(90 - Dec); ICRF to ecliptic is Rx(-obliquity).
 */
export function planeToEcliptic(
  el: PlaneElements,
  poleRaDeg: number,
  poleDecDeg: number,
): PlaneElements {
  const { normal, peri } = orbitAxes(el.i * DEG, el.node * DEG, el.omega * DEG);
  const toEcliptic = (v: V3): V3 =>
    rotX(rotZ(rotX(v, Math.PI / 2 - poleDecDeg * DEG), poleRaDeg * DEG + Math.PI / 2), -OBLIQUITY);
  const n = toEcliptic(normal);
  const p = toEcliptic(peri);
  const i = Math.acos(Math.max(-1, Math.min(1, n[2])));
  const nodeRad = Math.abs(Math.sin(i)) < 1e-12 ? 0 : Math.atan2(n[0], -n[1]);
  const nodeDir: V3 = [Math.cos(nodeRad), Math.sin(nodeRad), 0];
  const omegaRad = Math.atan2(dot(cross(nodeDir, p), n), dot(nodeDir, p));
  return { i: deg360(i), node: deg360(nodeRad), omega: deg360(omegaRad) };
}

/** Unit vector in the J2000 ecliptic for a direction given as RA/Dec (degrees) in ICRF. */
export function equatorialToEclipticUnit(raDeg: number, decDeg: number): V3 {
  const ra = raDeg * DEG,
    dec = decDeg * DEG;
  return rotX(
    [Math.cos(dec) * Math.cos(ra), Math.cos(dec) * Math.sin(ra), Math.sin(dec)],
    -OBLIQUITY,
  );
}
